import React, { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, Send, Loader2 } from "lucide-react";

const STATUS = { scheduled: "bg-stone-100 text-stone-600", sent: "bg-blue-50 text-blue-700", completed: "bg-green-50 text-green-700", overdue: "bg-red-50 text-red-700", missed: "bg-red-50 text-red-700", skipped: "bg-stone-50 text-stone-400" };
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";

export const PatientCheckins = ({ patientId, token }) => {
  const [checkins, setCheckins] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/staff/patient/${patientId}/checkins`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setCheckins(d.checkins || [])).catch(() => setCheckins([]));
  }, [patientId, token]);
  useEffect(load, [load]);

  const send = async (c) => {
    setBusy(c.checkin_id);
    try {
      const r = await fetch(`${API}/staff/checkins/${c.checkin_id}/send`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (r.ok && d.emailed) toast.success(d.message); else toast.error(d.detail || d.message || "Could not send.");
      load();
    } catch { toast.error("Connection problem."); } finally { setBusy(null); }
  };

  if (!checkins) return <Loader2 className="w-5 h-5 animate-spin text-[#FF7A00]" />;
  if (!checkins.length) return <p className="text-sm text-stone-400 text-center py-4" data-testid="no-checkins">No check-ins scheduled. They are created when the patient starts the 12-week programme.</p>;

  return (
    <div className="divide-y divide-stone-100" data-testid="patient-checkins-list">
      {checkins.map(c => (
        <div key={c.checkin_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2.5" data-testid={`patient-checkin-${c.week_number}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-stone-800 w-24 shrink-0">{c.checkin_type === "programme_review" ? "Week 12 review" : `Week ${c.week_number}`}</span>
            <Badge variant="outline" className={`text-[10px] capitalize ${STATUS[c.status] || ""}`}>{c.status}</Badge>
            <span className="text-xs text-stone-400 truncate">{c.status === "completed" ? `Submitted ${fmt(c.completed_at)}` : c.sent_at ? `Sent ${fmt(c.sent_at)}` : `Due to send ${fmt(c.send_date)}`}</span>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" className="rounded-lg h-8 text-xs" onClick={() => window.open(`/checkin/${c.token}?preview=1`, "_blank")} data-testid={`preview-checkin-${c.week_number}`}>
              <Eye className="w-3.5 h-3.5 mr-1" />Preview
            </Button>
            {!["completed", "skipped"].includes(c.status) && (
              <Button size="sm" className="rounded-lg h-8 text-xs bg-[#FF7A00] hover:bg-[#E56E00] text-white" disabled={busy === c.checkin_id} onClick={() => send(c)} data-testid={`send-checkin-${c.week_number}`}>
                {busy === c.checkin_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}{c.status === "scheduled" ? "Send now" : "Resend"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
